import React, { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MdOutlineNavigateBefore, MdOutlineNavigateNext, MdStyle } from 'react-icons/md';
import { IoColorPaletteOutline } from 'react-icons/io5';
import { TbRulerMeasure } from 'react-icons/tb';
import { FaGlobeAmericas, FaMapMarkedAlt, FaCity, FaMapMarkerAlt } from 'react-icons/fa';
import { useColorMode } from '@chakra-ui/react';
import { useBusinessType } from '../../contexts/BusinessType';
import { useUser } from 'contexts';

export default function OtherTabs({ handleOtherClick }) {
   const { colorMode } = useColorMode();
   const { user } = useUser();
   const admin = user?.isAdmin;
   const rights = user?.userRights?.find((ur) => ur?.companyId === user?.loginhistory?.companyId);
   const isDark = colorMode === 'dark';
   const params = useParams();
   const tabListRef = useRef(null);
   // const { businessTypes } = useBusinessType();
   const businessType = localStorage?.getItem('businessTypes');
   const isMobile = businessType?.toUpperCase() === 'MOBILE';

   const tabs = [
      ...((admin || rights?.color?.view) ? [{ label: 'Color', value: 'color', path: 'masters/other/color', icon: <IoColorPaletteOutline size={18} /> }] : []),
      ...((admin || rights?.style?.view) && !isMobile ? [{ label: 'Style', value: 'style', path: 'masters/other/style', icon: <MdStyle size={18} /> }] : []),
      ...((admin || rights?.size?.view) && !isMobile ? [{ label: 'Size', value: 'size', path: 'masters/other/size', icon: <TbRulerMeasure size={18} /> }] : []),
      ...((admin || rights?.country?.view) ? [{ label: 'Countries', value: 'countries', path: 'masters/other/countries', icon: <FaGlobeAmericas size={18} /> }] : []),
      ...((admin || rights?.states?.view) ? [{ label: 'States', value: 'states', path: 'masters/other/states', icon: <FaMapMarkedAlt size={18} /> }] : []),
      ...((admin || rights?.city?.view) ? [{ label: 'City', value: 'cities', path: 'masters/other/cities', icon: <FaCity size={18} /> }] : []),
      ...((admin || rights?.area?.view) ? [{ label: 'Area', value: 'area', path: 'masters/other/area', icon: <FaMapMarkerAlt size={18} /> }] : []),
   ];

   const getCurrentTabIndex = () => {
      for (let i = 0; i < tabs.length; i++) {
         if (params['*'] === tabs[i].path || params['*'].startsWith(`${tabs[i].path}/`)) {
            return i;
         }
      }
      return 0;
   };

   const [activeTabIndex, setActiveTabIndex] = useState(getCurrentTabIndex());
   useEffect(() => setActiveTabIndex(getCurrentTabIndex()), [params['*']]);

   if (tabs.length === 0) return null;

   const prevTab = activeTabIndex > 0 ? tabs[activeTabIndex - 1] : null;
   const currentTab = tabs[activeTabIndex];
   const nextTab = activeTabIndex < tabs.length - 1 ? tabs[activeTabIndex + 1] : null;

   const getVisibleTabs = (count) => {
      const tabsOnEachSide = Math.floor(count / 2);
      let startIdx = Math.max(0, activeTabIndex - tabsOnEachSide);
      let endIdx = startIdx + count - 1;
      if (endIdx >= tabs.length) {
         endIdx = tabs.length - 1;
         startIdx = Math.max(0, endIdx - count + 1);
      }
      return tabs.slice(startIdx, endIdx + 1);
   };

   const NavButton = ({ disabled, onClick, children }) => (
      <button
         onClick={onClick}
         disabled={disabled}
         className={`p-2 rounded-full transition-all duration-300 ${disabled
            ? 'opacity-30 cursor-not-allowed'
            : `cursor-pointer hover:scale-110 ${isDark
               ? 'hover:bg-slate-800 text-slate-300 hover:text-white'
               : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
            }`
            }`}>
         {children}
      </button>
   );

   const TabButton = ({ tab, isActive, onClick }) => (
      <button
         onClick={() => onClick(tab.value)}
         className={`flex items-center justify-center gap-2 px-6 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${isActive
            ? `${isDark
               ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-lg ring-2 ring-purple-400/50'
               : 'bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 text-white shadow-lg ring-2 ring-emerald-300/50'
            } scale-110`
            : `${isDark
               ? 'text-gray-400 hover:text-white hover:bg-slate-800/70'
               : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/70'
            } hover:scale-105`
            }`}>
         <div className={`flex items-center justify-center transition-transform ${isActive ? 'scale-125' : ''}`}>
            {tab.icon}
         </div>
         <span className="whitespace-nowrap">{tab.label}</span>
      </button>
   );

   if (!params['*'].startsWith('masters/other')) return null;

   return (
      <div>
         <div
            className={`mb-3 rounded-2xl p-2.5 px-4 overflow-hidden ${isDark
               ? 'bg-slate-900/60 backdrop-blur-lg border border-slate-800/80'
               : 'bg-white border border-gray-200 shadow-xl shadow-gray-100/40'
               }`}>
            {/* Large screens - all tabs */}
            <div className="xl:flex items-center gap-3" ref={tabListRef}>
               {tabs.map((tab) => (
                  <TabButton
                     key={tab.value}
                     tab={tab}
                     isActive={params['*'].startsWith(tab.path)}
                     onClick={handleOtherClick}
                  />
               ))}
            </div>

            {/* Desktop - 7 tabs */}
            <div className="hidden lg:flex xl:hidden items-center justify-between gap-2">
               <NavButton
                  disabled={activeTabIndex === 0}
                  onClick={() => handleOtherClick(tabs[activeTabIndex - 1].value)}>
                  <MdOutlineNavigateBefore size={24} />
               </NavButton>
               <div className="flex items-center gap-1 flex-1 justify-center">
                  {getVisibleTabs(7).map((tab) => (
                     <TabButton
                        key={tab.value}
                        tab={tab}
                        isActive={tab.value === currentTab.value}
                        onClick={handleOtherClick}
                     />
                  ))}
               </div>
               <NavButton
                  disabled={activeTabIndex === tabs.length - 1}
                  onClick={() => handleOtherClick(tabs[activeTabIndex + 1].value)}>
                  <MdOutlineNavigateNext size={24} />
               </NavButton>
            </div>

            {/* Tablet - 5 tabs */}
            <div className="hidden md:flex lg:hidden items-center justify-between gap-2">
               <NavButton
                  disabled={activeTabIndex === 0}
                  onClick={() => handleOtherClick(tabs[activeTabIndex - 1].value)}>
                  <MdOutlineNavigateBefore size={24} />
               </NavButton>
               <div className="flex items-center gap-1 flex-1 justify-center">
                  {getVisibleTabs(5).map((tab) => (
                     <TabButton
                        key={tab.value}
                        tab={tab}
                        isActive={tab.value === currentTab.value}
                        onClick={handleOtherClick}
                     />
                  ))}
               </div>
               <NavButton
                  disabled={activeTabIndex === tabs.length - 1}
                  onClick={() => handleOtherClick(tabs[activeTabIndex + 1].value)}>
                  <MdOutlineNavigateNext size={24} />
               </NavButton>
            </div>

            {/* Mobile - single tab + prev/next */}
            <div className="flex md:hidden items-center justify-between gap-2">
               {prevTab ? (
                  <NavButton onClick={() => handleOtherClick(prevTab.value)}>
                     <MdOutlineNavigateBefore size={22} />
                  </NavButton>
               ) : (
                  <div className="w-8" />
               )}
               <div
                  className={`flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm shadow-xl ${isDark
                     ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white ring-2 ring-purple-400/50'
                     : 'bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 text-white ring-2 ring-emerald-300/50'
                     }`}>
                  {currentTab.icon}
                  <span className="truncate">{currentTab.label}</span>
               </div>
               {nextTab ? (
                  <NavButton onClick={() => handleOtherClick(nextTab.value)}>
                     <MdOutlineNavigateNext size={22} />
                  </NavButton>
               ) : (
                  <div className="w-8" />
               )}
            </div>
         </div>
      </div>
   );
}
